import React, { useLayoutEffect, useRef, useState } from 'react'
import { ReviewProps } from '../types/review'
import ReviewReceiptCard from './ReviewReceiptCard'
import SlideWidget from './SlideWidget'

interface ReviewSlideCardProps {
  reviews: ReviewProps[]
  onCurrentIndexChange?: (index: number) => void
}

/**
 * 좌우에 놓이는 빈 영수증.
 *
 * 영수증은 한줄평·태그·키워드 유무에 따라 길이가 제각각이다. 그대로 두면
 * 좌우 카드의 톱니가 서로 다른 높이에 흩어져 화면 아래쪽이 어수선했다.
 * 내용을 비우고 높이를 가운데 카드에 맞추면 톱니가 한 줄로 맞는다.
 *
 * 왼쪽 것은 좌우로 뒤집는다. receipt-bg.png는 그림자가 그림 안에 구워져
 * 있는데 오른쪽에만 있다(가장자리 밝기 240 → 183). 여백도 왼 23px·오른 14px로
 * 다르다. 그대로 쓰면 좌우 간격이 같은데도 오른쪽이 더 붙어 보인다.
 */
const BlankReceipt: React.FC<{ height: number | null; flip?: boolean }> = ({
  height,
  flip = false,
}) => (
  <div className="relative w-full" style={{ height: height ?? undefined }}>
    <img
      src="/images/receipt-bg.png"
      alt=""
      aria-hidden
      className="absolute top-0 left-0 w-full h-full object-fill"
      style={flip ? { transform: 'scaleX(-1)' } : undefined}
    />
  </div>
)

const ReviewSlideCard: React.FC<ReviewSlideCardProps> = ({
  reviews,
  onCurrentIndexChange,
}) => {
  /*
   * 가운데 카드의 높이를 재서 좌우에 물려준다.
   *
   * useLayoutEffect로 그리기 직전에 잰다. useEffect면 한 프레임 동안
   * 좌우 종이가 이전 높이로 보였다가 바뀌어 깜빡인다.
   */
  const centerRef = useRef<HTMLDivElement | null>(null)
  const [centerHeight, setCenterHeight] = useState<number | null>(null)

  useLayoutEffect(() => {
    const el = centerRef.current
    if (!el) return

    const measure = () => setCenterHeight(el.offsetHeight)
    measure()

    // 사진·글꼴이 늦게 자리를 잡아 높이가 바뀌는 경우가 있다
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  })
  /* 0건이면 Reviews가 이 컴포넌트를 아예 안 그린다(renderReviewSlideCard).
     빈 상태는 거기서 목록·이미지 두 탭이 공유하는 칸에 하나만 둔다. */

  const handleIndexChange = (index: number) => {
    if (onCurrentIndexChange) {
      onCurrentIndexChange(index)
    }
  }

  return (
    <div className="w-full flex flex-col items-center shrink-0">
      <div className="w-full">
        <SlideWidget
          items={reviews}
          itemWidth={324}
          spacingMultiplier={0.2}
          loop={reviews.length > 1}
          onIndexChange={handleIndexChange}
          renderItem={(review, _index, _style, position) =>
            position === 'center' ? (
              <div ref={centerRef}>
                <ReviewReceiptCard
                  placeName={review.restaurant.name}
                  placeAddress={review.placeAddress ?? ''}
                  placeCategory={review.restaurant.category}
                  score={review.restaurant.rating}
                  hideScore={review.hideScore || false}
                  dateDisplay={review.dateDisplay}
                  visitDate={review.date}
                  createdAt={review.date}
                  visitNumber={review.visitNumber}
                  visitType={review.visitType}
                  reviewText={review.reviewText || null}
                  taggedFriends={review.taggedFriends ?? []}
                  minPricePerPerson={review.minPricePerPerson ?? null}
                  maxPricePerPerson={review.maxPricePerPerson ?? null}
                  keywords={review.keywords ?? []}
                />
              </div>
            ) : (
              // 왼쪽 카드는 뒤집어 그림자가 바깥(왼쪽)으로 가게 한다
              <BlankReceipt height={centerHeight} flip={position === 'left'} />
            )
          }
        />
      </div>
    </div>
  )
}

export default ReviewSlideCard
